import { type RefObject, useCallback, useEffect, useRef } from "react";

/** Distance from the bottom, in px, still treated as "pinned to the latest". */
const SCROLL_PIN_THRESHOLD = 80;

interface UseChatAutoScrollOptions {
  /** Changes when a message is added or a thread is swapped in — always jumps. */
  jumpKey: number | string;
  /** Changes as the latest answer grows — follows only while pinned. */
  followKey: number | string;
}

interface UseChatAutoScrollReturn {
  scrollRef: RefObject<HTMLDivElement>;
  /** Wire to the scroller's onScroll — this is what records the pin state. */
  handleScroll: () => void;
}

/**
 * Keeps a chat scroller on the newest message.
 *
 * Pin state is recorded from the scroll event, NOT measured inside the effect:
 * by the time an effect runs the new text is already in the DOM, so a long
 * answer would read as "user scrolled far up" and the follow would stop dead on
 * exactly the answers that need it most.
 *
 * A new message always wins over the pin — sending a question, or opening a
 * thread from history, must land on the latest turn wherever the user was.
 */
export const useChatAutoScroll = ({
  jumpKey,
  followKey,
}: UseChatAutoScrollOptions): UseChatAutoScrollReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
    isPinnedRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isPinnedRef.current = distanceFromBottom <= SCROLL_PIN_THRESHOLD;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: jumpKey is the trigger, not a value the effect reads
  useEffect(() => {
    scrollToBottom();
  }, [jumpKey, scrollToBottom]);

  // Follow the answer as it grows, but only while the user is already at the
  // bottom — otherwise scrolling up to re-read would fight the stream.
  // biome-ignore lint/correctness/useExhaustiveDependencies: followKey is the trigger, not a value the effect reads
  useEffect(() => {
    if (!isPinnedRef.current) return;

    scrollToBottom();
  }, [followKey, scrollToBottom]);

  return { scrollRef, handleScroll };
};
